import React from 'react';
import { motion } from 'framer-motion';
import {
  Phone, Mail, CreditCard, DollarSign, Calendar
} from 'lucide-react';

const avatarColors = [
  'bg-primary-500', 'bg-emerald-500', 'bg-amber-500', 'bg-red-500',
  'bg-purple-500', 'bg-pink-500', 'bg-cyan-500', 'bg-indigo-500',
];

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map((p) => p.charAt(0)).join('').toUpperCase().slice(0, 2);
}

function getColor(name) {
  if (!name) return avatarColors[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return avatarColors[Math.abs(hash) % avatarColors.length];
}

export default function CustomerCard({
  customer,
  onAddPayment,
  onViewLedger,
  onSendReminder,
}) {
  const isHighCredit = (customer.currentCredit || 0) > 50000;

  return (
    <motion.div
      whileHover={{ y: -2, boxShadow: '0 8px 25px rgba(0,0,0,0.08)' }}
      transition={{ duration: 0.2 }}
      className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm"
    >
      <div className="flex items-start gap-4">
        <div className={`w-14 h-14 rounded-full ${getColor(customer.name)} flex items-center justify-center text-white font-bold text-lg flex-shrink-0`}>
          {getInitials(customer.name)}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white truncate">
              {customer.name}
            </h3>
          </div>

          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
            {customer.phone && (
              <span className="flex items-center gap-1">
                <Phone size={11} /> {customer.phone}
              </span>
            )}
            {customer.email && (
              <span className="flex items-center gap-1">
                <Mail size={11} /> {customer.email}
              </span>
            )}
            {customer.lastPurchaseDate && (
              <span className="flex items-center gap-1">
                <Calendar size={11} /> Last: {new Date(customer.lastPurchaseDate).toLocaleDateString('en-PK', { month: 'short', day: 'numeric' })}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-4">
        <div className={`p-3 rounded-lg ${isHighCredit ? 'bg-red-50 dark:bg-red-900/20' : 'bg-gray-50 dark:bg-gray-700'}`}>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Current Credit</p>
          <p className={`text-lg font-bold ${isHighCredit ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
            Rs. {(customer.currentCredit || 0).toLocaleString()}
          </p>
        </div>
        <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Total Purchases</p>
          <p className="text-lg font-bold text-gray-900 dark:text-white">
            Rs. {(customer.totalPurchases || 0).toLocaleString()}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => onAddPayment?.(customer)}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-colors"
        >
          <DollarSign size={14} />
          Add Payment
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => onViewLedger?.(customer)}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
        >
          <CreditCard size={14} />
          Ledger
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => onSendReminder?.(customer)}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
        >
          <Mail size={14} />
          Remind
        </motion.button>
      </div>
    </motion.div>
  );
}
